/**
 * BulkAddItemSelect
 * Step 1 of the unified bulk add flow.
 * Matches the existing Add-flow UX: dialog-based item picker + dialog-based location picker.
 * Items are searched/filtered across all 6 types; a single location is shared.
 */

import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, InputAdornment, Table, TableHead, TableBody,
  TableRow, TableCell, IconButton, Checkbox, Chip, Stack, CircularProgress,
  useTheme, alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  products:  '#1565c0',
  chemicals: '#7b1fa2',
  caps:      '#00796b',
  bottles:   '#2e7d32',
  labels:    '#e65100',
  supplies:  '#5d4037',
};

const TYPE_LABELS = {
  products:  'Product',
  chemicals: 'Chemical',
  caps:      'Cap',
  bottles:   'Bottle',
  labels:    'Label',
  supplies:  'Supply',
};

const ALL_TYPES = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];

// ── Helper: SearchableDialog ───────────────────────────────────────────────────

function SelectionButton({ label, placeholder, children, onClick, hasValue }) {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
        {label}
      </Typography>
      <Button
        fullWidth
        variant="outlined"
        onClick={onClick}
        sx={{
          justifyContent: hasValue ? 'flex-start' : 'space-between',
          textAlign: 'left',
          minHeight: 56,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderColor: alpha(theme.palette.primary.main, 0.3),
          color: hasValue ? 'text.primary' : 'text.secondary',
          fontWeight: 500,
          px: 2, py: 1.5,
          textTransform: 'none',
          fontSize: '0.9rem',
          display: 'block',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
          }
        }}
      >
        {hasValue ? children : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{placeholder}</span>
            <SearchIcon sx={{ fontSize: 20, opacity: 0.5 }} />
          </Box>
        )}
      </Button>
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function BulkAddItemSelect({
  allItems,
  loading,
  selectedItems,
  onSelectedItemsChange,
  selectedLocation,
  onLocationChange,
  locations
}) {
  const theme = useTheme();

  // Item dialog state
  const [itemDialogOpen,    setItemDialogOpen]    = useState(false);
  const [tempSelected,      setTempSelected]      = useState([]);
  const [itemSearch,        setItemSearch]        = useState('');
  const [typeFilter,        setTypeFilter]        = useState('all');

  // Location dialog state
  const [locDialogOpen, setLocDialogOpen] = useState(false);
  const [locSearch,     setLocSearch]     = useState('');

  // Build flat list with type tag
  const flatItems = useMemo(() => {
    const result = [];
    for (const [type, items] of Object.entries(allItems)) {
      for (const item of (items || [])) {
        result.push({ ...item, inventoryType: type });
      }
    }
    return result;
  }, [allItems]);

  // Filtered items inside the dialog
  const dialogItems = useMemo(() => {
    const q = itemSearch.toLowerCase();
    return flatItems.filter(item => {
      const matchType   = typeFilter === 'all' || item.inventoryType === typeFilter;
      const matchSearch = !q ||
        item.name?.toLowerCase().includes(q) ||
        item.sku?.toLowerCase().includes(q);
      return matchType && matchSearch;
    });
  }, [flatItems, itemSearch, typeFilter]);

  const filteredLocations = useMemo(() => {
    const q = locSearch.toLowerCase();
    return [...locations]
      .filter(l => l.name?.toLowerCase().includes(q))
      .sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main)  return 1;
        return 0;
      });
  }, [locations, locSearch]);

  const isTempSelected = (item) =>
    tempSelected.some(s => s.id === item.id && s.inventoryType === item.inventoryType);

  const toggleTemp = (item) => {
    if (isTempSelected(item)) {
      setTempSelected(prev => prev.filter(s => !(s.id === item.id && s.inventoryType === item.inventoryType)));
    } else {
      setTempSelected(prev => [...prev, item]);
    }
  };

  const openItemDialog = () => {
    setTempSelected(selectedItems);
    setItemSearch('');
    setTypeFilter('all');
    setItemDialogOpen(true);
  };

  const confirmItemSelection = () => {
    onSelectedItemsChange(tempSelected);
    setItemDialogOpen(false);
  };

  // Type chip counts
  const typeCounts = useMemo(() => {
    const counts = { all: flatItems.length };
    for (const type of ALL_TYPES) {
      counts[type] = (allItems[type] || []).length;
    }
    return counts;
  }, [flatItems, allItems]);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Select the items and location for this bulk add session
      </Typography>

      <Grid container spacing={3}>
        {/* Items */}
        <Grid item xs={12}>
          <SelectionButton
            label="Items (Multiple Types Supported)"
            placeholder="Select items to add stock to"
            onClick={openItemDialog}
            hasValue={selectedItems.length > 0}
          >
            <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
              {selectedItems.map(item => {
                const color = TYPE_COLORS[item.inventoryType] || '#666';
                return (
                  <Chip
                    key={`${item.inventoryType}:${item.id}`}
                    label={item.name}
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      bgcolor: alpha(color, 0.1),
                      color,
                      border: `1px solid ${alpha(color, 0.3)}`,
                      fontWeight: 500
                    }}
                  />
                );
              })}
            </Stack>
          </SelectionButton>
        </Grid>

        {/* Location */}
        <Grid item xs={12}>
          <SelectionButton
            label="Destination Location"
            placeholder="Select Location"
            onClick={() => { setLocSearch(''); setLocDialogOpen(true); }}
            hasValue={!!selectedLocation}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectedLocation?.name}
            </Typography>
          </SelectionButton>
        </Grid>
      </Grid>

      {/* ── Item Selection Dialog ──────────────────────────────────────────── */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: '75vh', width: '90vw', maxWidth: '1400px' } }}
        sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          pb: 2.5, pt: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Select Items</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose from any inventory type — products, chemicals, caps, bottles, labels, supplies
            </Typography>
          </Box>
          <IconButton onClick={() => setItemDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {/* Type filter chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3, mt: 2 }}>
            {['all', ...ALL_TYPES].map(type => {
              const active = typeFilter === type;
              const color  = TYPE_COLORS[type];
              const label  = type === 'all'
                ? `All Types (${typeCounts.all})`
                : `${TYPE_LABELS[type]}s (${typeCounts[type]})`;
              return (
                <Chip
                  key={type}
                  label={label}
                  onClick={() => setTypeFilter(type)}
                  variant={active ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    fontWeight: active ? 500 : 400,
                    fontSize: '0.8rem',
                    bgcolor: active && type !== 'all' ? alpha(color, 0.15) : undefined,
                    borderColor: type !== 'all' ? alpha(color, 0.6) : undefined,
                    color: type !== 'all' ? color : undefined
                  }}
                />
              );
            })}
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search by name or SKU..."
            value={itemSearch}
            onChange={e => setItemSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              )
            }}
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1rem' } }}
          />

          {/* Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table sx={{ '& .MuiTableCell-root': { fontSize: '0.95rem', py: 2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, width: 56 }} />
                  <TableCell sx={{ fontWeight: 500, width: 120 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 500, width: 150 }}>SKU</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogItems.map(item => {
                  const key     = `${item.inventoryType}:${item.id}`;
                  const selected = isTempSelected(item);
                  const color   = TYPE_COLORS[item.inventoryType];
                  return (
                    <TableRow
                      key={key}
                      hover
                      onClick={() => toggleTemp(item)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : undefined,
                        '&:hover': {
                          bgcolor: selected
                            ? alpha(theme.palette.primary.main, 0.10)
                            : alpha(theme.palette.grey[100], 0.8),
                          transform: 'translateX(4px)',
                          boxShadow: `inset 4px 0 0 ${selected ? theme.palette.primary.main : theme.palette.grey[400]}`
                        }
                      }}
                    >
                      <TableCell width={56} align="center">
                        <Checkbox
                          checked={selected}
                          onClick={e => { e.stopPropagation(); toggleTemp(item); }}
                          sx={{
                            color: alpha(color, 0.5),
                            '&.Mui-checked': { color }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={TYPE_LABELS[item.inventoryType]}
                          size="small"
                          sx={{
                            height: 22, fontSize: '0.7rem',
                            bgcolor: alpha(color, 0.12), color,
                            fontWeight: 500,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: selected ? 500 : 400 }}>{item.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {item.sku || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {dialogItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" sx={{ py: 4, fontSize: '1rem' }}>
                        {itemSearch ? 'No items match your search' : 'No items available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button
            onClick={() => setItemDialogOpen(false)}
            sx={{ borderRadius: 2, px: 3, py: 1.2, fontSize: '0.95rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmItemSelection}
            disabled={tempSelected.length === 0}
            sx={{ borderRadius: 2, px: 4, py: 1.2, fontSize: '0.95rem', fontWeight: 500 }}
          >
            Confirm Selection ({tempSelected.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Location Selection Dialog ──────────────────────────────────────── */}
      <Dialog
        open={locDialogOpen}
        onClose={() => setLocDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: '60vh', width: '80vw', maxWidth: '1200px' } }}
        sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          pb: 3, pt: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>Select Location</Typography>
          <IconButton onClick={() => setLocDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            placeholder="Search locations..."
            value={locSearch}
            onChange={e => setLocSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ mt: 3, mb: 4, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1rem' } }}
          />
          <Table sx={{ '& .MuiTableCell-root': { fontSize: '1rem', py: 2.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLocations.map(loc => (
                <TableRow
                  key={loc.id}
                  hover
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                >
                  <TableCell>{loc.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        onLocationChange(loc);
                        setLocDialogOpen(false);
                        setLocSearch('');
                      }}
                      sx={{ borderRadius: 2, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLocations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography color="text.secondary" sx={{ py: 4, fontSize: '1rem' }}>
                      No locations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
